import { EnumMapper } from "@indietabletop/appkit";
import type {
  ActionEffect,
  DefenseEffect,
  MomentumLimitEffect,
  SpecialRuleEffect,
} from "./engine/Effect";
import {
  type ActionData,
  type AnyArmorRoll,
  type ItemProfileData,
  type UpgradeProfileData,
  type WeaponProfileData,
} from "./engine/Ruleset";

const ArmorRollFmt = EnumMapper.from<AnyArmorRoll["type"]>({
  CRIT_FAIL: "Pierced",
  FAIL: "Struck",
  SAVE: "Impervious!",
});

function inches(value: string) {
  if (!value) {
    return "";
  }

  return `${value}″`;
}

function modifier(num: number) {
  const sign = num > 0 ? "+" : "-";
  return num !== 0 ? `${sign}${num}` : "";
}

function parens(value: string) {
  if (value) {
    return ` (${value})`;
  }

  return "";
}

function range({ min, max }: { min: number; max: number }) {
  if (min === max) {
    return `${min}`;
  }

  return `${min}–${max}`;
}

export function moveAction(effect: {
  action: { name: string };
  modifier: number;
}) {
  return `${effect.action.name}${parens(inches(modifier(effect.modifier)))}`;
}

export function attackAction(effect: {
  action: { name: string };
  modifier: number;
}) {
  return `${effect.action.name}${parens(modifier(effect.modifier))}`;
}

export function armorRoll(roll: AnyArmorRoll) {
  return `${ArmorRollFmt.get(roll.type)}${parens(range(roll))}`;
}

export function piercedRoll(rolls: AnyArmorRoll[]) {
  const pierced = rolls.find((roll) => roll.type === "CRIT_FAIL");

  if (!pierced) {
    return "";
  }

  return `Armor table rolls of ${range(pierced)} count as pierced.`;
}

export function defenseOption(effect: {
  defense: { name: string };
  modifier: number;
}) {
  return `${effect.defense.name}${parens(modifier(effect.modifier))}`;
}

const WeaponTypeFmt = EnumMapper.from<WeaponProfileData["type"]>({
  ONE_HANDED: "One-handed Weapon",
  TWO_HANDED: "Two-handed Weapon",
  TWO_HANDED_RANGED: "Two-handed Ranged Weapon",
});

export function weaponType(type: WeaponProfileData["type"]) {
  return WeaponTypeFmt.get(type);
}

const ItemTypeFmt = EnumMapper.from<ItemProfileData["type"]>({
  WORN: "Worn Item",
  USABLE: "Use Item",
  CONSUMABLE_INSTANT: "Consumable (Instant)",
  CONSUMABLE_BUFF: "Consumable (Buff)",
  CONSUMABLE_TOSSED: "Consumable (Tossed)",
});

export function itemType(type: ItemProfileData["type"]) {
  return ItemTypeFmt.get(type);
}

const UpgradeTypeFmt = EnumMapper.from<UpgradeProfileData["type"]>({
  ARMOR_UPGRADE: "Armor Upgrade",
  WEAPON_UPGRADE: "Weapon Upgrade",
  SHIELD_UPGRADE: "Shield Upgrade",
});

export function upgradeType(type: UpgradeProfileData["type"]) {
  return UpgradeTypeFmt.get(type);
}

const cardinalRules = new Intl.PluralRules("en-GB");

// English only uses "one" and "other"
type EnglishCardinalRule = Extract<Intl.LDMLPluralRule, "one" | "other">;

export function plural(
  count: number,
  rules: Record<EnglishCardinalRule, string>,
) {
  const tag = cardinalRules.select(count) as EnglishCardinalRule;
  return rules[tag].replace("#", count.toString());
}

const ConjunctionFmt = new Intl.ListFormat("en-US", {
  style: "long",
  type: "conjunction",
});

export function conjunction(items: (string | null | undefined)[]) {
  return ConjunctionFmt.format(items.filter((i): i is string => !!i));
}

const DisjunctionFmt = new Intl.ListFormat("en-US", {
  style: "long",
  type: "disjunction",
});

export function disjunction(items: string | string[]) {
  return DisjunctionFmt.format(items);
}

export function effect(
  e: ActionEffect | DefenseEffect | SpecialRuleEffect | MomentumLimitEffect,
) {
  function usesInches(action: ActionData | ActionData[]) {
    return Array.isArray(action)
      ? action.some((e) => e.unit === "INCHES")
      : action.unit === "INCHES";
  }

  switch (e.type) {
    case "ADD_SPECIAL_RULE": {
      if (e.data.hideInSummary) {
        return "";
      }

      return e.rule;
    }

    case "REMOVE_DEFENSE": {
      const defenseOption = Array.isArray(e.defense)
        ? disjunction(e.defense.map((d) => d.name.toLocaleLowerCase()))
        : e.defense.name.toLocaleLowerCase();

      return `Cannot ${defenseOption} defense.`;
    }

    case "GAIN_ACTION": {
      const count = Array.isArray(e.action) ? e.action.length : 1;
      const action = plural(count, { one: "action", other: "actions" });
      const target = Array.isArray(e.action)
        ? conjunction(e.action.map((a) => a.name.toLocaleLowerCase()))
        : e.action.name.toLocaleLowerCase();

      return `Gain ${target} ${action}.`;
    }

    case "ADD_ACTION_MODIFIER": {
      const benefit = usesInches(e.action)
        ? inches(modifier(e.value))
        : modifier(e.value);

      const target = Array.isArray(e.action)
        ? conjunction(e.action.map((a) => a.name.toLocaleLowerCase()))
        : e.action.name.toLocaleLowerCase();

      const count = Array.isArray(e.action) ? e.action.length : 1;
      const action = plural(count, { one: "action", other: "actions" });

      return `Gain ${benefit} to ${target} ${action}.`;
    }

    case "ADD_DEFENSE_MODIFIER": {
      const benefit = modifier(e.value);

      const target = Array.isArray(e.defense)
        ? conjunction(e.defense.map((d) => d.name.toLocaleLowerCase()))
        : e.defense.name.toLocaleLowerCase();

      const count = Array.isArray(e.defense) ? e.defense.length : 1;
      const defense = plural(count, { one: "defense", other: "defenses" });

      return `Gain ${benefit} to ${target} ${defense}.`;
    }

    case "ADD_MOMENTUM_LIMIT_MODIFIER": {
      const changed = e.value < 0 ? "decreased" : "increased";
      return `Momentum limit ${changed} by ${Math.abs(e.value)}.`;
    }

    default: {
      return "";
    }
  }
}

export const FeatureEnum = EnumMapper.from({
  GREATHELM_CORE_RULES: "Core Rules",
});
